import { useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { makeAuthedRequest } from "shared/utils/makeAuthedRequest";
import { TOOL_TYPE } from "shared/enums";

export function useTools({ contact }) {
  const queryClient = useQueryClient();
  const { isLoading, isError, data, error } = useQuery("/prospects/tools");

  const byType = useMemo(() => {
    const sorted = {
      audio: [],
      book: [],
      bov: [],
    };

    if (data) {
      for (const record of data) {
        switch (record.type) {
          case TOOL_TYPE.Audio:
            sorted.audio.push(record);
            break;
          case TOOL_TYPE.Book:
            sorted.book.push(record);
            break;
          case TOOL_TYPE.BOV:
            sorted.bov.push(record);
            break;
        }
      }
    }

    return sorted;
  }, [data]);

  const currentStepAudio = useMemo(
    () =>
      data
        ? contact.handouts
            .filter(
              (x) => x.type === TOOL_TYPE.Audio && x.wasGivenInCurrentStep
            )
            .map((x) => ({
              ...x,
              ...data.find((tool) => tool.id === x.toolId),
            }))
        : null,
    [contact.handouts, data]
  );

  const currentStepBooks = useMemo(
    () =>
      data
        ? contact.handouts
            .filter((x) => x.type === TOOL_TYPE.Book && x.wasGivenInCurrentStep)
            .map((x) => ({
              ...x,
              ...data.find((tool) => tool.id === x.toolId),
            }))
        : null,
    [contact.handouts, data]
  );

  const currentStepBOV = useMemo(
    () =>
      data
        ? contact.handouts
            .filter((x) => x.type === TOOL_TYPE.BOV && x.wasGivenInCurrentStep)
            .map((x) => ({
              ...x,
              ...data.find((tool) => tool.id === x.toolId),
            }))
        : null,
    [contact.handouts, data]
  );

  const addToolMutation = useMutation(
    (toolId) =>
      makeAuthedRequest("/prospects/handout", {
        method: "post",
        body: JSON.stringify({
          contactId: contact.id,
          toolId,
        }),
      }),
    {
      onSettled: () => {
        queryClient.invalidateQueries(`/contacts/${contact.id}`);
        queryClient.invalidateQueries("/prospects/bsm");
      },
    }
  );

  const deleteToolMutation = useMutation(
    (opts) =>
      makeAuthedRequest(
        `/prospects/handout/${contact.id}/${opts.handoutId}/${
          opts.undo ? "true" : "false"
        }`,
        { method: "delete" }
      ),
    {
      onSettled: () => {
        queryClient.invalidateQueries(`/contacts/${contact.id}`);
        queryClient.invalidateQueries("/prospects/bsm");
      },
    }
  );

  return {
    isLoading,
    isError,
    data,
    error,

    ...byType,

    currentStepAudio,
    currentStepBooks,
    currentStepBOV,

    addToolMutation,
    deleteToolMutation,
  };
}
